import React, { useState } from 'react';
import { Box, Typography, Card, CardContent, CircularProgress, Grid, useTheme, useMediaQuery, Tooltip } from '@mui/material';
import { AiOutlineInfoCircle } from 'react-icons/ai';
import { useQuery } from '@tanstack/react-query';
import { fetchIdleCircuitsSummary, fetchIdleCircuitsTrend } from '../../api/dashboardApi';
import { LineChart, PieChart } from '@mui/x-charts';
import IdleCircuitsDetailModal from './IdleCircuitsDetailModal';

const formatCurrency = (value) => {
  return `$${Number(value).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const IdleCircuitsOverview = () => {
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [selectedMonths, setSelectedMonths] = useState(3);
  
  const theme = useTheme();
  const isLargeScreen = useMediaQuery(theme.breakpoints.up('md'));

  const { data: summaryData, isLoading: summaryLoading } = useQuery({
    queryKey: ['idleCircuitsSummary'],
    queryFn: () => fetchIdleCircuitsSummary(),
    staleTime: 5 * 60 * 1000,
  });

  const { data: trendData, isLoading: trendLoading } = useQuery({
    queryKey: ['idleCircuitsTrend'],
    queryFn: () => fetchIdleCircuitsTrend(),
    staleTime: 5 * 60 * 1000,
  });

  const handleBreakdownClick = (event, item) => {
    const breakdown = summaryData?.data?.breakdown || [];
    if (item && item.dataIndex !== undefined) {
      const data = breakdown[item.dataIndex];
      if (data && data.months_idle_gte) {
        setSelectedMonths(data.months_idle_gte);
        setDetailModalOpen(true);
      }
    }
  };

  if (summaryLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  const breakdown = summaryData?.data?.breakdown || [];
  const agingBuckets = summaryData?.data?.aging_buckets || [];
  const totalCircuits = summaryData?.data?.total_circuits_with_billing_history || 0;
  const trend = trendData?.data?.trend || [];

  // Transform Data for Doughnut
  const pieData = agingBuckets.map((b, index) => ({
    id: index,
    value: b.count,
    label: b.bucket
  }));

  // Transform Data for Dual Area (Breakdown)
  const breakdownLabels = breakdown.map(b => `${b.months_idle_gte} Month${b.months_idle_gte !== 1 ? 's' : ''}`);
  const breakdownCounts = breakdown.map(b => b.count);
  const breakdownSpend = breakdown.map(b => b.last_known_monthly_spend_usd);

  // Transform Data for Trend
  const trendMonths = trend.map(t => t.month);
  const trendCounts = trend.map(t => t.count);
  const trendSpend = trend.map(t => t.last_known_monthly_spend_usd);

  return (
    <Box sx={{ mt: 2, mb: 1 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" fontWeight="bold">Idle Circuits Analysis</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2" color="textSecondary" fontWeight="bold">
            Total Circuits Monitored: {totalCircuits}
          </Typography>
          <Tooltip 
            title={
              <React.Fragment>
                Every circuit that has ever been billed a recurring monthly charge. Some of these are currently active (billed this month); the rest are currently idle — see the charts below for the breakdown.<br/><br/>
                Verification: Click any point on the charts below to see the exact list of circuits behind these numbers, including each one's last billed month and invoice.
              </React.Fragment>
            } 
            arrow 
            placement="top"
          >
            <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', color: '#7C2EA0' }}>
              <AiOutlineInfoCircle size={16} />
            </Box>
          </Tooltip>
        </Box>
      </Box>

      <Grid container spacing={2}>
        {/* Aging Buckets - Doughnut Chart */}
        <Grid item xs={12} md={4}>
          <Card elevation={0} sx={{ border: '1px solid #e2e8f0', height: '100%', borderRadius: 2 }}>
            <CardContent sx={{ pb: 1, pt: 2, px: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Typography variant="subtitle2" fontWeight={600}>Aging Distribution</Typography>
                <Tooltip 
                  title={
                    <React.Fragment>
                      How long today's idle circuits have been idle, grouped into bands: 1–2 months, 3–5 months, 6–11 months, and 12+ months. A circuit idle 12+ months is a much stronger signal it's been abandoned than one idle just 1–2 months, which could still be a temporary billing gap.<br/><br/>
                      Verification: These four bands add up to the "idle ≥ 1 month" count shown at the 1 Month point of the Cumulative Impact Drop-off chart — click that point to see the full list, then group by "Months Idle" to match each band's count.
                    </React.Fragment>
                  } 
                  arrow 
                  placement="top"
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', color: '#7C2EA0' }}>
                    <AiOutlineInfoCircle size={16} />
                  </Box>
                </Tooltip>
              </Box>
              <Box sx={{ height: 220, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {agingBuckets.length > 0 ? (
                  <PieChart
                    series={[
                      {
                        data: pieData,
                        innerRadius: isLargeScreen ? 45 : 40,
                        outerRadius: isLargeScreen ? 80 : 70,
                        paddingAngle: 5,
                        cornerRadius: 5,
                      }
                    ]}
                    margin={isLargeScreen ? { right: 130 } : { top: 10, bottom: 60, left: 10, right: 10 }}
                    slotProps={{
                      legend: {
                        direction: isLargeScreen ? 'column' : 'row',
                        position: isLargeScreen 
                          ? { vertical: 'middle', horizontal: 'right' } 
                          : { vertical: 'bottom', horizontal: 'middle' },
                        padding: 0,
                        labelStyle: { fontSize: 12 }
                      }
                    }}
                  />
                ) : (
                  <Typography color="textSecondary">No data available</Typography>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Breakdown Drop-off - Dual Area Chart */}
        <Grid item xs={12} md={8}>
          <Card elevation={0} sx={{ border: '1px solid #e2e8f0', height: '100%', borderRadius: 2 }}>
            <CardContent sx={{ pb: 1, pt: 2, px: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0 }}>
                <Typography variant="subtitle2" fontWeight={600}>
                  Cumulative Impact Drop-off (Click to view details)
                </Typography>
                <Tooltip 
                  title={
                    <React.Fragment>
                      Each point shows how many circuits have been idle for at least that many months (purple, left axis), and what they used to cost per month before going idle (red line, right axis) — money that may no longer need to be paid if these circuits are confirmed discontinued.<br/><br/>
                      Verification: Click any point to open the full circuit list for that threshold — circuit ID, location, last billed month, last invoice, and last known monthly amount.
                    </React.Fragment>
                  } 
                  arrow 
                  placement="top"
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', color: '#7C2EA0' }}>
                    <AiOutlineInfoCircle size={16} />
                  </Box>
                </Tooltip>
              </Box>
              <Box sx={{ height: 220 }}>
                {breakdown.length > 0 ? (
                  <LineChart
                    margin={{ left: 50, right: 60, top: 30, bottom: 20 }}
                    xAxis={[{ scaleType: 'point', data: breakdownLabels, label: 'Threshold (Months Idle)', tickLabelStyle: { fontSize: 11 } }]}
                    yAxis={[
                      { id: 'countAxis', scaleType: 'linear', tickLabelStyle: { fontSize: 11 } },
                      { id: 'spendAxis', scaleType: 'linear', tickLabelStyle: { fontSize: 11 } }
                    ]}
                    series={[
                      { 
                        data: breakdownCounts, 
                        label: 'Circuits', 
                        color: '#7C2EA0', 
                        yAxisId: 'countAxis', 
                        area: true, 
                        showMark: true 
                      },
                      { 
                        data: breakdownSpend, 
                        label: 'Previous Spend', 
                        color: '#EF4444', 
                        yAxisId: 'spendAxis', 
                        showMark: true 
                      }
                    ]}
                    rightAxis="spendAxis"
                    onMarkClick={handleBreakdownClick}
                    slotProps={{
                      legend: {
                        direction: 'row',
                        position: { vertical: 'top', horizontal: 'middle' },
                        labelStyle: { fontSize: 12 },
                        itemMarkWidth: 10,
                        itemMarkHeight: 10
                      }
                    }}
                  />
                ) : (
                  <Box sx={{ display: 'flex', height: '100%', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography color="textSecondary">No data available</Typography>
                  </Box>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Trend Chart */}
        {/* <Grid item xs={12}>
          <Card elevation={0} sx={{ border: '1px solid #e2e8f0', height: '100%', borderRadius: 2, backgroundColor: '#f8fafc' }}>
            <CardContent sx={{ pb: 1, pt: 2, px: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0 }}>
                <Typography variant="subtitle2" fontWeight={600}>
                  Historical Trend (Idle ≥ {trendData?.data?.threshold_months_idle_gte || 2} Months)
                </Typography>
                <Tooltip 
                  title={
                    <React.Fragment>
                      Tracks one fixed group — circuits idle 2 or more months — over the last 6 calendar months, so you can see whether idle circuits are piling up or being cleared out. Bars show the count each month; the line shows their combined last-known monthly spend.<br/><br/>
                      Verification: Compare this month's bar/line against the "2 Months" point on the Cumulative Impact Drop-off chart above — they should match, since both come from the same idle ≥ 2 months definition.
                    </React.Fragment>
                  } 
                  arrow 
                  placement="top"
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', color: '#7C2EA0' }}>
                    <AiOutlineInfoCircle size={16} />
                  </Box>
                </Tooltip>
              </Box>
              <Box sx={{ height: 220 }}>
                {trendLoading ? (
                  <Box sx={{ display: 'flex', height: '100%', alignItems: 'center', justifyContent: 'center' }}>
                    <CircularProgress size={24} />
                  </Box>
                ) : trend.length > 0 ? (
                  <LineChart
                    margin={{ left: 50, right: 60, top: 30, bottom: 20 }}
                    xAxis={[{ scaleType: 'point', data: trendMonths, label: 'Month', tickLabelStyle: { fontSize: 11 } }]}
                    yAxis={[
                      { id: 'countAxis', scaleType: 'linear', tickLabelStyle: { fontSize: 11 } },
                      { id: 'spendAxis', scaleType: 'linear', tickLabelStyle: { fontSize: 11 } }
                    ]}
                    series={[
                      { data: trendCounts, label: 'Circuits Count (Bar)', color: '#94a3b8', yAxisId: 'countAxis', type: 'bar' },
                      { data: trendSpend, label: 'Last Spend (Line)', color: '#334155', yAxisId: 'spendAxis', type: 'line' }
                    ]}
                    rightAxis="spendAxis"
                    slotProps={{
                      legend: {
                        direction: 'row',
                        position: { vertical: 'top', horizontal: 'middle' },
                        labelStyle: { fontSize: 12 },
                        itemMarkWidth: 10,
                        itemMarkHeight: 10
                      }
                    }}
                  />
                ) : (
                  <Box sx={{ display: 'flex', height: '100%', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography color="textSecondary">No data available</Typography>
                  </Box>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid> */}
      </Grid>

      <IdleCircuitsDetailModal 
        open={detailModalOpen} 
        onClose={() => setDetailModalOpen(false)} 
        months={selectedMonths} 
      />
    </Box>
  );
};

export default IdleCircuitsOverview;
